import React, {
 useCallback,
 useEffect,
 useMemo,
 useState,
 Suspense,
} from "react";
import {
 deleteItem,
 deleteMultipleItem,
 fetchData,
 toggleStatus,
} from "../../../utils/apiUtils";
import {
 ColumnAdjust,
 DeleteButton,
 PlusButton,
} from "../../../helper/ImageFormat";
import DeleteModel, {
 confirmBulkDelete,
} from "../../../Components/DeleteModel";
import Breadcrumb from "../../../helper/Breadcrumb";
import CurrencyTable from "./CurrencyTable";
import ColumnVisibilitySidebar from "../../../Components/ColumnVisibilitySidebar";
import { currencyColumnsList } from "../../../Data/columnsData";
import PerPageSelect from "../../../Components/PerPageSelect";
import TableSearch from "../../../Components/TableSearch";
import { useNavigate } from "react-router-dom";

const Currency = () => {
 const navigator = useNavigate();
 const [loading, setLoading] = useState(true);
 const [currencies, setCurrencies] = useState([]);
 const [search, setSearch] = useState("");
 const [deleteItemId, setDeleteItemId] = useState({});
 const [totalCount, setTotalCount] = useState(0);
 const [currentPage, setCurrentPage] = useState(1);
 const [perPage, setPerPage] = useState(10);
 const [deleteModalOpen, setDeleteModalOpen] = useState(false);
 const [selected, setSelected] = useState([]);
 const [isDeleting, setIsDeleting] = useState(false);
 const [sidebarOpen, setSidebarOpen] = useState(false);
 const [columnsList, setColumnsList] = useState(currencyColumnsList);
 const totalPages = useMemo(
  () => Math.ceil(totalCount / perPage),
  [totalCount, perPage]
 );
 const handleToggleColumn = (key) => {
  setColumnsList((prev) =>
   prev.map((col) =>
    col.key === key ? { ...col, visible: !col.visible } : col
   )
  );
 };

 const fetchCurrencies = useCallback(
  async (page = 1, searchQuery = "") => {
   setLoading(true);
   await fetchData(
    "currency-pagination",
    { perPage, pageNo: page, search: searchQuery },
    setCurrencies,
    setTotalCount,
    setCurrentPage,
    setLoading
   );
  },
  [perPage]
 );

 useEffect(() => {
  const delay = search ? 500 : 0;
  const handler = setTimeout(() => {
   fetchCurrencies(currentPage, search);
  }, delay);
  return () => clearTimeout(handler);
 }, [search, perPage]);

 const handleDelete = (data) => {
  setDeleteItemId(data);
  setDeleteModalOpen(true);
 };

 const confirmDelete = async () => {
  setIsDeleting(true);
  try {
   const res = await deleteItem("currency", deleteItemId?.id, fetchCurrencies);
   setDeleteItemId({});
   setDeleteModalOpen(false);
   return res;
  } catch (error) {
   return error;
  } finally {
   setIsDeleting(false);
  }
 };

 const handleStatusToggle = async (currency) => {
  const data = await toggleStatus(
   "currency-status",
   currency?.id,
   fetchCurrencies
  );
  if (Object.keys(data).length > 0) {
   setCurrencies((prev) =>
    prev.map((curr) =>
     curr.id === currency.id ? { ...curr, isActive: data?.isActive } : curr
    )
   );
  }
 };

 const handlePageClick = (page) => {
  if (page >= 1 && page <= totalPages) {
   fetchCurrencies(page);
  }
 };

 const breadcrumbdata = [
  { label: "Home", path: "/" },
  { label: "Currency", path: "/currency" },
 ];

 const handlebulkDelete = async () => {
  await confirmBulkDelete({
   itemType: "Currency",
   selectedIds: selected,
   deleteApiCall: (ids) => deleteMultipleItem("bulk/currency", ids),
   refreshCallback: fetchCurrencies,
  });
 };

 const handleAdd = () => {
  navigator("/add-currency");
 };

 const handleEditClick = (item) => {
  navigator("/add-currency", {
   state: item,
  });
 };

 return (
  <>
   <div className="content-wrapper">
    <div className="page-header mt-5 d-flex justify-content-between">
     <h3 className="custom-page-title">Currency</h3>
     <Breadcrumb items={breadcrumbdata} />
    </div>

    <div
     className="d-flex justify-content-between mb-2 bg-white p-2"
     style={{ marginTop: "-20px" }}
    >
     <PerPageSelect value={perPage} onChange={setPerPage} />

     <div style={{ display: "flex", gap: "10px" }}>
      <TableSearch value={search} onChange={setSearch} />
      <PlusButton handleAdd={handleAdd} module={"Currency"} />
      {selected?.length > 0 && (
       <DeleteButton
        handleAdd={handlebulkDelete}
        name={"selected currency"}
        module={"Currency"}
       />
      )}
      <ColumnAdjust handleOpen={() => setSidebarOpen(true)} />
     </div>
    </div>

    <CurrencyTable
     loading={loading}
     currencies={currencies}
     currentPage={currentPage}
     perPage={perPage}
     totalPages={totalPages}
     totalCount={totalCount}
     handlePageClick={handlePageClick}
     handleEditClick={handleEditClick}
     handleDelete={handleDelete}
     handleStatusToggle={handleStatusToggle}
     selected={selected}
     setSelected={setSelected}
     columnsList={columnsList}
    />
   </div>

   {sidebarOpen && (
    <ColumnVisibilitySidebar
     columns={columnsList}
     onToggle={handleToggleColumn}
     onClose={() => setSidebarOpen(false)}
    />
   )}

   <Suspense fallback={<div>Loading...</div>}>
    <DeleteModel
     deleteModalOpen={deleteModalOpen}
     setDeleteModalOpen={() => setDeleteModalOpen(!deleteModalOpen)}
     onConfirm={confirmDelete}
     itemName="currency"
     isDeleting={isDeleting}
    />
   </Suspense>
  </>
 );
};

export default Currency;
